import React, { useEffect, useRef, useState, useMemo } from 'react'
import {
  Typography,
  Card,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  OutlinedInput,
  FormControl,
  InputLabel,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { removeDoNotTranslateStyle } from 'commons/form'
import RichTextDisplay from 'app/components/richTextEditor/RichTextDisplay'
import { usePlace } from 'commons/usePlace'
import clsx from 'clsx'
import Topic from 'app/main/composeMessage/preview/v4/Topic'
import {
  prop,
  compose,
  join,
  ifElse,
  view,
  lensPath,
  isEmpty,
  isNil,
  always,
  map,
  propEq,
  path,
  both,
  complement,
  propSatisfies,
  any,
  filter,
  find,
  includes,
  length,
  __,
  trim,
} from 'ramda'
import Assets from 'app/configs/assetConfigs'
import { FormattedMessage, useIntl } from 'react-intl'
import useMessageExpiration from 'commons/useMessageExpiration'
import { FuseAnimateGroup } from '@fuse'
import { isEmptyOrNil } from 'libs/empty'
import Loader from 'app/components/Loader'
import { useTranslationPreviewLanguage } from '../useTranslationPreviewLanguage'
import { LanguageSubscriberCount } from 'app/components/selectOptional'
import ErrorTranslationMessage from 'app/components/ErrorTranslationMessage'
import moment from 'moment-timezone'
import { optionPreview, previewKey } from 'app/constants/previewCompose'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import apiConfig from 'app/configs/apiConfigs'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'
import { htmlToPlainText } from 'libs/text'
import useDebounce from 'libs/useDebounce'
import RadioSelector from 'app/componentV4/selectors/RadioSelector'
import MessageTypeChip from 'app/componentV4/MessageTypeChip'
import MessagePriorityChip from 'app/componentV4/MessagePriorityChip'
import { getPlainText } from 'commons/plainText'
import apiConfigs from 'app/configs/apiConfigs'
import GiftVoucherIcon from 'app/components/icons/GiftVoucherIcon'
import PlayCircleFilledWhiteOutlinedIcon from '@material-ui/icons/PlayCircleFilledWhiteOutlined'
import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined'
import {
  NOTI_PRIORITY_EMERGENCY,
  NOTI_PRIORITY_URGENT,
  NOTI_PRIORITY_HIGH,
} from 'app/constants/priority'
import { equals } from 'sanctuary'
import PreviewEventAction from './PreviewEventAction'
import PreviewEventDate from './PreviewEventDate'
import PreviewEventPlace from './PreviewEventPlace'
import PreviewAction from './PreviewAction'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import Attachments from '../../attachments/attachments'
import useFeatureFlag from 'app/configs/useFeatureFlag'
import { isImageLoading } from '../../attachments/attachmentsHelpers'

export const viewLogo = view(lensPath(['info', 'logo']))
export const viewName = view(lensPath(['name']))

const useStyles = makeStyles((theme) => ({
  cardEmptyPreview: {
    backgroundColor: '#FAFDFF',
  },
  previewLabel: {
    color: '#C4C4C4',
  },
  previewStyle: {
    borderRadius: 0,
    margin: '1rem',
  },
  placeName: { color: theme.palette.secondary.main },
  placeLogoImage: {
    objectFit: 'cover',
    objectPosition: 'center',
    overflow: 'hidden',
    borderRadius: '50%',
  },
  directionRtl: {
    direction: 'rtl',
  },
  textColorBlur: {
    fontSize: '12px',
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    color: '#247bcd',
  },
  dotColor: {
    color: '#00314b',
  },
  voucherTicket: {
    position: 'relative',
    width: '100%',
  },
  voucherTicketText: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: '30%',
    left: '10%',
  },
  ticketTitle: {
    fontStyle: 'italic',
    fontSize: '14px',
    display: 'block',
    lineHeight: '1.14',
  },
  ticketSubtitle: {
    fontSize: '18px',
    display: 'block',
    lineHeight: '1.14',
  },
  title_category_1: {
    color: '#FFF',
  },
  subtitle_category_1: {
    color: '#006296',
  },
  title_category_2: {
    color: '#444',
  },
  subtitle_category_2: {
    color: '#950',
  },
  title_category_3: {
    color: '#444',
  },
  subtitle_category_3: {
    color: '#444',
  },
  title_category_4: {
    color: '#FF9',
  },
  subtitle_category_4: {
    color: '#FFF',
  },
  title_category_5: {
    color: '#444',
  },
  subtitle_category_5: {
    color: '#F79',
  },
  labelSelected: {
    transform: 'translate(18px, 18px) scale(1) !important',
  },
  languageSelected: {
    '& .MuiSelect-select:focus': {
      backgroundColor: 'unset !important',
    },
  },
  formLabel: {
    '& .MuiFormControlLabel-label': {
      width: '100%',
      color: '#000000 !important',
    },
  },
  seeMore: {
    '& .twoLinePadding': {
      paddingLeft: '2rem',
      paddingRight: '10rem',
      textIndent: '-2rem',
    },
    '& .twoLinePadding::first-line': {
      color: 'red',
    },
  },
  responsiveImageContainer: {
    '& img': {
      maxWidth: '100% !important',
      height: 'auto !important',
      width: '100% !important',
      objectFit: 'contain',
    },
    '& .tiptap-responsive-img': {
      maxWidth: '100% !important',
      height: 'auto !important',
      width: '100% !important',
      objectFit: 'contain',
    },
    // For mobile devices, ensure images take full width
    [theme.breakpoints.down('sm')]: {
      '& img, & .tiptap-responsive-img': {
        maxWidth: '100% !important',
        height: 'auto !important',
        width: '100% !important',
        objectFit: 'contain',
      },
    },
  },
}))

const isNotNilOrEmpty = both(complement(isNil), complement(isEmpty))
const hasValueOption = any(propSatisfies(isNotNilOrEmpty, 'title'))

function getVisibleCharCount(el, text) {
  if (!el || !el.firstChild) return 0
  const node = el.firstChild
  const range = document.createRange()

  let low = 0
  let high = text.length
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2)
    range.setStart(node, 0)
    range.setEnd(node, mid)

    const rect = range.getBoundingClientRect()
    const pRect = el.getBoundingClientRect()

    if (rect.height > pRect.height) {
      high = mid - 1
    } else {
      low = mid
    }
  }
  return low
}

const PreviewMessage = (props) => {
  const [viewInSelected, setViewInSelected] = useState(previewKey.homeFeed)
  const containerRef = useRef(null)
  const measureRef = useRef(null)
  const [visibleCount, setVisibleCount] = useState(0)
  const { formatMessage } = useIntl()
  const { notification } = props
  const classes = useStyles()
  const place = usePlace()
  const placeLogo = viewLogo(place)
  const placeName = viewName(place)
  const placeTopics = prop('topics', notification)
  const messageExpiration = useMessageExpiration(notification)
  const isMessageExpiration = messageExpiration.hasExpiration
  const isPoll = propEq('type', TYPE_POLL, notification)
  const isGeneral = propEq('type', TYPE_GENERAL, notification)
  const isEvent = propEq('type', TYPE_EVENT, notification)
  const isTtsEnabled = prop('isTtsEnabled', notification)
  const title = prop('title', notification)
  const body = isPoll
    ? prop('additionalContext', notification)
    : prop('body', notification)
  const question = isPoll ? prop('question', notification) : undefined
  const options = isPoll ? prop('options', notification) : undefined
  const mediaSelected = notification.mediaSocials.find(
    (item) => item.isSelected,
  )
  const { mes_4367_attachment_gallery } = useFeatureFlag()
  const { mes_3198_poll_preview_translation } = useFeatureFlag()
  const {
    state,
    apiFetchersLanguagesCallbackCompose,
    updateOriginalText,
    updateLanguageSelected,
    translateText,
    onCloseLanguageSelected,
    clearLanguageSelected,
    onOpenSelected,
  } = useTranslationPreviewLanguage()

  const subGroupIds = notification.recipients.subGroups
  const subscriberIds = notification.recipients.subscribers.map(
    (item) => item.id,
  )

  const topicIds = compose(
    join(','),
    ifElse(isNil, always([]), map(prop('id'))),
    prop('topics'),
  )(notification)

  const channelIds = compose(
    ifElse(isNil, always(''), join(',')),
    path(['channels', 'channelIds']),
  )(notification)

  // Debounce question and options to prevent excessive API calls (500ms delay)
  const debouncedQuestion = useDebounce(question, 500)
  const debouncedOptions = useDebounce(options, 500)

  // Create a stable key for options array to detect actual changes (deep comparison)
  // Preserve order since translation mapping uses index position
  const optionsKey = useMemo(() => {
    if (!options || !Array.isArray(options)) return ''
    return JSON.stringify(
      options.map((opt) => (opt && opt.title ? opt.title.trim() : '')),
    )
  }, [options])

  const isShowTranslator = isPoll
    ? mes_3198_poll_preview_translation &&
      (!isEmpty(subGroupIds) || !isEmpty(subscriberIds)) &&
      !isEmpty(channelIds) &&
      !isEmptyOrNil(htmlToPlainText(debouncedQuestion)) &&
      hasValueOption(debouncedOptions || [])
    : (!isEmpty(subGroupIds) || !isEmpty(subscriberIds)) &&
      !isEmpty(channelIds) &&
      !isEmptyOrNil(htmlToPlainText(title)) &&
      !isEmptyOrNil(htmlToPlainText(body))

  const handleOriginalText = () => {
    const topics = map(prop('name'), placeTopics)
    setVisibleCount(0)

    const payload =
      isPoll && mes_3198_poll_preview_translation
        ? {
            title: '',
            body,
            topics,
            question: debouncedQuestion,
            options: debouncedOptions,
          }
        : { title, body, topics }

    updateOriginalText(payload)
  }

  const handleTranslationText = () => {
    const topics = map(prop('name'), placeTopics)
    const baseTranslateParams = {
      languageId: state.languageId,
      translates: state.translates,
      topicIds,
      topics,
    }

    const shouldTranslatePoll =
      mes_3198_poll_preview_translation &&
      isPoll &&
      debouncedQuestion &&
      hasValueOption(debouncedOptions || []) &&
      !isNil(state.languageId)

    const shouldTranslateGeneral =
      !isPoll && title && body && !isNil(state.languageId)

    if (shouldTranslatePoll) {
      translateText({
        ...baseTranslateParams,
        title,
        body,
        question: debouncedQuestion,
        options: debouncedOptions,
      })
    } else if (shouldTranslateGeneral) {
      translateText({
        ...baseTranslateParams,
        title,
        body,
      })
    }
  }

  // Helper to extract option titles for API
  const extractPollOptionTitles = (options) => {
    if (!Array.isArray(options)) return []
    return compose(
      map(compose(trim, prop('title'))),
      filter((opt) => {
        const title = prop('title', opt)
        return title && trim(title) !== ''
      }),
      filter(Boolean),
    )(options)
  }

  const fetchLanguageOrClear = () => {
    if (isPoll) {
      handleOriginalText()
    }

    const shouldFetchLanguages =
      channelIds && (subGroupIds || subscriberIds) && isEmpty(state.languages)

    const baseApiParams = {
      subGroupIds: isEmpty(subGroupIds) ? '' : join(',', subGroupIds),
      subscriberIds: isEmpty(subscriberIds) ? '' : join(',', subscriberIds),
      topicIds,
      channelIds,
      composeTitle: title,
      composeBody: body,
    }

    if (shouldFetchLanguages) {
      const apiParams =
        mes_3198_poll_preview_translation && isPoll
          ? {
              ...baseApiParams,
              composeQuestion: debouncedQuestion,
              composeOptions: extractPollOptionTitles(debouncedOptions),
            }
          : baseApiParams

      apiFetchersLanguagesCallbackCompose(apiParams)
    } else {
      if (mes_3198_poll_preview_translation && isPoll) {
        handleOriginalText()
        onOpenSelected(debouncedQuestion, debouncedOptions)
      } else {
        onOpenSelected()
      }
    }
  }

  const showLanguageSelected = (languageId) => {
    return compose(
      prop('name'),
      find(propEq('id', languageId)),
    )(state.languages)
  }

  const showOptionPoll = (options) => {
    const limitOption =
      viewInSelected === previewKey.homeFeed && options.length > 3
        ? options.slice(0, 3)
        : options

    const isImageEmpty = compose(
      equals(0),
      length,
      filter(
        (item) => !isEmptyOrNil(item.imagePath) && isNotNilOrEmpty(item.title),
      ),
    )(limitOption)

    return limitOption
      .filter((item) => isNotNilOrEmpty(item.title))
      .map((item, index) => (
        <RadioGroup
          className="w-full text-primary-300"
          key={`option-${index + 1}`}
        >
          <FormControlLabel
            value={item.title}
            className={clsx(classes.formLabel, 'text-primary-300')}
            control={<Radio disabled />}
            label={
              <div className="flex justify-between w-full">
                <Typography className="flex-1 my-auto text-primary-300">
                  {item.title}
                </Typography>
                <div className="p-2 items-center flex ">
                  {!isImageEmpty && isNotNilOrEmpty(item.imagePath) && (
                    <img
                      src={`${apiConfig.cndUrl}/${item.imagePath}`}
                      alt={item.title}
                      className="w-44 h-44 rounded-8 border-1 border-primary-350"
                    />
                  )}
                  {!isImageEmpty && !isNotNilOrEmpty(item.imagePath) && (
                    <div className="flex items-center justify-center w-44 h-44 rounded-8 bg-primary-100 border-1 border-primary-350">
                      <ImageOutlinedIcon
                        fontSize="small"
                        className="text-primary-350"
                      />
                    </div>
                  )}
                </div>
              </div>
            }
          />
        </RadioGroup>
      ))
  }

  const getViewInLabel = () => {
    switch (viewInSelected) {
      case previewKey.homeFeed:
      case previewKey.messageDetail:
        return 'MessageSpring App'

      default:
        return 'Other Channels'
    }
  }

  const gePlainTextBody = () => {
    const body = state?.textDisplay?.body ?? ''
    if (!body) return ''

    return getPlainText(body)
  }

  const showPollEndDate = () => {
    const end = moment(notification.pollEndDateTime)
    const now = moment()
    const diffMs = end.diff(now)
    let unit = 'year'
    let value = 0

    if (diffMs <= 0) {
      unit = 'minute'
      value = 0
    } else if (diffMs < moment.duration(1, 'hour')) {
      unit = 'minute'
      value = Math.max(1, Math.ceil(moment.duration(diffMs).asMinutes()))
    } else if (diffMs < moment.duration(1, 'day')) {
      unit = 'hour'
      value = Math.ceil(moment.duration(diffMs).asHours())
    } else if (diffMs < moment.duration(1, 'year')) {
      unit = 'day'
      value = Math.ceil(moment.duration(diffMs).asDays())
    } else {
      unit = 'year'
      value = Math.ceil(moment.duration(diffMs).asYears())
    }

    return formatMessage(
      { id: 'compose.preview.remaining.pollEnd' },
      { value, unit },
    )
  }

  useEffect(() => {
    if (!measureRef.current) return
    const text = gePlainTextBody(state.textDisplay.body)
    const runMeasure = () => {
      const countText = getVisibleCharCount(measureRef.current, text)
      setVisibleCount(countText)
    }

    runMeasure()
    const resizeObserver = new ResizeObserver(() => {
      runMeasure()
    })
    resizeObserver.observe(measureRef.current)

    return () => resizeObserver.disconnect()
  }, [
    state.textDisplay.title,
    state.textDisplay.body,
    placeTopics.length,
    subGroupIds.length,
    subscriberIds.length,
    channelIds.length,
    viewInSelected,
  ])

  useEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure || state.isLoading) return

    if (visibleCount <= 0) {
      container.textContent = ''
      return
    }

    const newCount = Math.max(0, visibleCount - 15)
    const full = measure.textContent || ''
    const plain = gePlainTextBody(state.textDisplay.body) ?? ''

    if (full.length <= visibleCount) {
      container.textContent = plain
      return
    }

    const truncated = full.slice(0, newCount)
    const textNode = document.createTextNode(truncated)

    const seeMore = document.createElement('span')
    seeMore.className = 'underline'
    seeMore.textContent = `${formatMessage({ id: 'note.seeMore' })}`

    const dotNot = document.createTextNode('...')

    container.replaceChildren(textNode, dotNot, seeMore)
  }, [visibleCount, state.isLoading, viewInSelected, state.textDisplay.body])

  useEffect(handleOriginalText, [
    title,
    body,
    placeTopics.length,
    subGroupIds.length,
    subscriberIds.length,
    channelIds.length,
    mes_3198_poll_preview_translation && isPoll ? debouncedQuestion : undefined,
    mes_3198_poll_preview_translation && isPoll ? optionsKey : undefined,
  ])

  useEffect(handleTranslationText, [
    state.languageId,
    mes_3198_poll_preview_translation && isPoll ? debouncedQuestion : undefined,
    mes_3198_poll_preview_translation && isPoll ? optionsKey : undefined,
    mes_3198_poll_preview_translation && isPoll ? undefined : title,
    mes_3198_poll_preview_translation && isPoll ? undefined : body,
  ])

  useEffect(() => {
    clearLanguageSelected()
  }, [
    subGroupIds.length,
    subscriberIds.length,
    channelIds,
    topicIds,
    title,
    body,
    mes_3198_poll_preview_translation && isPoll ? debouncedQuestion : undefined,
    mes_3198_poll_preview_translation && isPoll ? optionsKey : undefined,
  ])

  return (
    <div className="text-blue-950">
      <Typography variant="h1" className="font-medium text-16 text-blue-950">
        <FormattedMessage id="translationPreview.initialNoti.title" />
      </Typography>
      <div className="mt-5">
        {isShowTranslator && (
          <div className="py-4">
            <Typography className="text-blue-950 text-14 font-medium">
              <FormattedMessage id="translationPreview.translate.title" />
            </Typography>
            <FormControl className="w-full mt-4 bg-white text-14">
              {!state.languageId && (
                <InputLabel
                  htmlFor="languages"
                  className={clsx(
                    'm-auto text-blue-950',
                    classes.labelSelected,
                  )}
                >
                  <FormattedMessage id="translationPreview.translationSelectLabel" />
                </InputLabel>
              )}

              <Select
                name="languages"
                id="languages"
                open={state.openLanguageSelector}
                className={clsx(
                  'w-full h-48 rounded-8 text-blue-950',
                  classes.languageSelected,
                )}
                value={state.languageId || ''}
                onChange={updateLanguageSelected}
                onOpen={() => {
                  fetchLanguageOrClear()
                }}
                onClose={onCloseLanguageSelected}
                input={<OutlinedInput />}
              >
                {state.isLoading ? (
                  <MenuItem key="loading" value="" className="text-center">
                    ... Loading
                  </MenuItem>
                ) : (
                  state.languages.map((language) => (
                    <MenuItem
                      key={language.id}
                      value={language.id}
                      className="text-blue-950"
                    >
                      <LanguageSubscriberCount
                        name={language.name}
                        nativeName={language.nativeName}
                        count={language.count}
                      />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </div>
        )}

        <div className="flex justify-between my-5 items-center">
          <Typography className="text-blue-950 text-14 font-medium">
            <FormattedMessage
              id="compose.preview.viewIn"
              values={{ channel: getViewInLabel() }}
            />
          </Typography>
          <RadioSelector
            name="language"
            label="Language"
            value={viewInSelected}
            options={optionPreview}
            onChange={setViewInSelected}
          />
        </div>

        <Card className="relative border border-gray-200 shadow-0 rounded-8 p-5">
          <Loader
            isLoading={state.isLoading || state.isTranslating}
            placeholder={
              <FuseAnimateGroup
                enter={{ animation: 'transition.fadeIn' }}
                leave={{ animation: 'transition.fadeOut' }}
              >
                <div className="flex justify-center py-64">
                  <CircularProgress color="secondary" />
                </div>
              </FuseAnimateGroup>
            }
          >
            {isNil(state.error) ? (
              <div>
                {isMessageExpiration &&
                  viewInSelected !== previewKey.homeFeed && (
                    <div className="w-full my-5 border-0 bg-tertiary-300 rounded-8">
                      <div className="w-full p-5 flex items-center space-x-2">
                        <svg
                          className={clsx(
                            classes.expirationIcon,
                            'h-16 w-16 fill-current',
                          )}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M15 2h-3.5l-1-1h-5l-1 1H1v2h14zM16 9c-.7 0-1.37.1-2 .29V5H2v12c0 1.1.9 2 2 2h5.68A6.999 6.999 0 0 0 23 16c0-3.87-3.13-7-7-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                          <path d="M16.5 12H15v5l3.6 2.1.8-1.2-2.9-1.7z" />
                        </svg>
                        <span className="text-12 text-primary-300">
                          {messageExpiration.expirationDatePreview}
                        </span>
                      </div>
                    </div>
                  )}
                <div className="flex space-x-5">
                  <div className="w-64 h-64 flex-shrink-0">
                    <img
                      src={placeLogo}
                      className="w-full h-full rounded-8"
                      alt="Place logo"
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src = Assets.personSilhouetteImage
                      }}
                    />
                  </div>

                  <div className="justify-center text-blue-950 flex-1 min-w-0">
                    <Typography
                      className={clsx(
                        'font-medium text-14',
                        viewInSelected === previewKey.homeFeed
                          ? 'truncate'
                          : 'break-words',
                      )}
                    >
                      {placeName}
                    </Typography>
                    <Typography className="text-12 text-primary-300">
                      <FormattedMessage id="notificationPreview.placeholderTime" />
                    </Typography>
                    <div className="flex mt-2">
                      {viewInSelected !== previewKey.homeFeed && (
                        <MessageTypeChip
                          className="ltr:mr-6 rtl:ml-6"
                          type={notification.type}
                        />
                      )}

                      {viewInSelected === previewKey.homeFeed ? (
                        includes(notification.priority, [
                          NOTI_PRIORITY_URGENT.label,
                          NOTI_PRIORITY_EMERGENCY.label,
                          NOTI_PRIORITY_HIGH.label,
                        ]) && (
                          <MessagePriorityChip
                            priority={notification.priority}
                            className="ltr:ml-8 rtl:mr-8"
                          />
                        )
                      ) : (
                        <MessagePriorityChip
                          priority={notification.priority}
                          className="ltr:ml-8 rtl:mr-8"
                        />
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="flex justify-end space-x-4">
                      {isTtsEnabled && (
                        <PlayCircleFilledWhiteOutlinedIcon className="text-grey-200 text-20" />
                      )}
                      <MoreHorizOutlinedIcon className="text-grey-200 text-20" />
                    </div>
                  </div>
                </div>
                <div className="h-full mt-5">
                  <div className="flex flex-col">
                    <div
                      className={clsx(
                        'flex flex-col',
                        viewInSelected === previewKey.homeFeed
                          ? 'order-1'
                          : 'order-2',
                      )}
                    >
                      {(isGeneral || isEvent) && (
                        <div
                          dir="auto"
                          className={clsx(
                            'w-full text-15 break-words font-bold text-blue-950',
                            classes.responsiveImageContainer,
                          )}
                        >
                          <RichTextDisplay text={state.textDisplay.title} />
                        </div>
                      )}

                      <div
                        dir="auto"
                        className={clsx(
                          viewInSelected === previewKey.homeFeed && isPoll
                            ? 'order-1'
                            : 'order-2',
                          'w-full',
                        )}
                      >
                        <div className="break-words py-1">
                          {viewInSelected === previewKey.homeFeed ? (
                            <div className="text-14 text-primary-300 relative">
                              <p
                                ref={measureRef}
                                className="break-words py-4 whitespace-pre-wrap w-full absolute top-0 left-0 line-clamp-2"
                                style={{
                                  visibility: 'hidden',
                                  zIndex: -1,
                                }}
                              >
                                {gePlainTextBody()}
                              </p>
                              <p
                                ref={containerRef}
                                className="break-words py-4 whitespace-pre-wrap"
                              ></p>
                              {mediaSelected &&
                                !isImageLoading(mediaSelected) &&
                                !isEvent &&
                                !isPoll && (
                                  <div className="w-full flex justify-center items-center overflow-hidden min-h-[100px] max-h-[400px] rounded-8">
                                    <img
                                      src={`${apiConfigs.cndUrl}/${mediaSelected.url}`}
                                      alt="Image home feed preview"
                                      className="w-full h-full object-cover object-center rounded-8"
                                    />
                                  </div>
                                )}
                            </div>
                          ) : (
                            <div
                              className={clsx(
                                'py-4 text-14',
                                classes.responsiveImageContainer,
                              )}
                            >
                              <RichTextDisplay
                                text={
                                  state.textDisplay.body
                                    ? removeDoNotTranslateStyle(
                                        state.textDisplay.body,
                                      )
                                    : ''
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {isPoll && (
                        <div
                          className={clsx(
                            'rounded-8 p-5',
                            viewInSelected === previewKey.homeFeed
                              ? 'order-2'
                              : 'order-1',
                            (hasValueOption(notification.options) ||
                              notification.question) &&
                              'border-1 border-primary-350 ',
                          )}
                        >
                          <div dir="auto" className="w-full">
                            <div className="text-14 text-blue-950">
                              {state.textDisplay.question !== undefined &&
                              state.textDisplay.question !== null &&
                              state.textDisplay.question !== ''
                                ? state.textDisplay.question
                                : notification.question}
                            </div>
                          </div>
                          {hasValueOption(notification.options) && (
                            <div dir="auto" className="w-full">
                              <div className="break-words w-full">
                                <div className="py-2">
                                  {showOptionPoll(
                                    state.textDisplay.options &&
                                      state.textDisplay.options.length > 0
                                      ? state.textDisplay.options
                                      : notification.options,
                                  )}
                                </div>
                                {viewInSelected === previewKey.homeFeed &&
                                  notification.options.length > 3 && (
                                    <div className="text-primary-300">
                                      <FormattedMessage id="label.and.more" />
                                      ...
                                    </div>
                                  )}
                              </div>
                              <div className="flex justify-between w-full py-5 items-center text-primary-300 text-12">
                                <div className="flex items-center text-12 text-primary-300 space-x-2">
                                  <label>
                                    <FormattedMessage
                                      id="compose.preview.remaining.voteCoune"
                                      values={{ count: 0 }}
                                    />
                                  </label>
                                  {!isEmptyOrNil(
                                    notification.pollEndDateTime,
                                  ) && (
                                    <div className="flex space-x-2">
                                      <label className="before:content-['•']  text-14 text-green-500"></label>
                                      <label className="text-green-500">
                                        {showPollEndDate()}
                                      </label>
                                    </div>
                                  )}
                                </div>
                                {viewInSelected === previewKey.homeFeed && (
                                  <div className="rounded-8 bg-secondary text-12 p-4 px-5 text-white">
                                    <FormattedMessage id="compose.preview.remaining.voteBtn" />
                                  </div>
                                )}
                              </div>
                              {viewInSelected !== previewKey.homeFeed && (
                                <div className="rounded-8 bg-neutrals-100 text-center text-12 p-5 px-5 text-grey-input">
                                  <FormattedMessage id="compose.preview.vote" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={clsx(
                        viewInSelected === previewKey.homeFeed
                          ? 'order-2'
                          : 'order-1',
                      )}
                    >
                      {isEvent && (
                        <div
                          className={clsx(
                            'border-1 border-primary-350 rounded-8',
                            viewInSelected === previewKey.messageDetail &&
                              'mb-5',
                          )}
                        >
                          <div>
                            {mediaSelected &&
                              !isImageLoading(mediaSelected) && (
                                <div
                                  className={clsx(
                                    'w-full flex justify-center items-center overflow-hidden min-h-[100px] max-h-[400px] rounded-8',
                                  )}
                                >
                                  <img
                                    src={`${apiConfigs.cndUrl}/${mediaSelected.url}`}
                                    alt="Image home feed preview"
                                    className={
                                      'w-full h-full object-cover object-center rounded-8'
                                    }
                                  />
                                </div>
                              )}
                            <PreviewEventDate
                              startDateTime={path(
                                ['event', 'startDateTime'],
                                notification,
                              )}
                              startTimezone={path(
                                ['event', 'startTimezone'],
                                notification,
                              )}
                              endDateTime={path(
                                ['event', 'endDateTime'],
                                notification,
                              )}
                              endTimezone={path(
                                ['event', 'endTimezone'],
                                notification,
                              )}
                            />
                            <PreviewEventPlace
                              placeName={path(
                                ['event', 'location'],
                                notification,
                              )}
                            />
                            <PreviewEventAction readonly={true} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {prop('voucher', notification) && (
                    <div className="flex w-full rounded-8 border-1 border-green-600 bg-green-50 text-green-600 p-5 my-5 items-center">
                      <div className="flex-1">
                        <div className="text-16 font-500">
                          <FormattedMessage id="compose.preview.voucher.title1" />
                        </div>
                        <div className="text-14">
                          <FormattedMessage id="compose.preview.voucher.title2" />
                        </div>
                      </div>
                      <div>
                        <GiftVoucherIcon />
                      </div>
                    </div>
                  )}

                  {mes_4367_attachment_gallery &&
                    viewInSelected === previewKey.messageDetail && (
                      <div className="my-8">
                        <Attachments
                          isPreview={true}
                          previewImages={notification.mediaSocials}
                        />
                      </div>
                    )}

                  <div className="flex items-end">
                    <PreviewAction notification={notification} />
                  </div>
                  {!isEmptyOrNil(state.topicsDisplay) &&
                    viewInSelected !== previewKey.homeFeed && (
                      <div dir="auto" className="mb-2">
                        <Grid container className="w-full">
                          {state.topicsDisplay.map((topic, index) => {
                            return (
                              <div className="mr-2" key={index + 1}>
                                <Topic id={index + 1} name={topic} />
                              </div>
                            )
                          })}
                        </Grid>
                      </div>
                    )}
                  {state.originalLanguageId !== state.languageId &&
                    viewInSelected !== previewKey.homeFeed && (
                      <div className="flex items-center text-12 text-primary-300 space-x-2">
                        <label>
                          <FormattedMessage
                            id="preview.voucher.authored"
                            values={{
                              language: showLanguageSelected(
                                state.originalLanguageId,
                              ),
                            }}
                          />
                        </label>
                        <label className="before:content-['•']  text-16"></label>
                        <label className="underline">
                          <FormattedMessage id="conversationRoom.translation.seeOriginal.label" />
                        </label>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div className="flex-col min-w-xs px-10 pb-10">
                <ErrorTranslationMessage />
              </div>
            )}
          </Loader>
        </Card>
      </div>
    </div>
  )
}

export default PreviewMessage
